import React, { useEffect, useMemo, useState } from 'react';
import md5 from 'md5';
import useSWR from 'swr';
import { toast } from 'react-toastify';
import { useLocation, useParams } from 'react-router-dom';
import { Form, Button, Card, Modal, Spinner, Alert } from 'react-bootstrap';
import api from '../../../services/api';
import { requestLaudoPresignDownload } from '../../../services/laudoFiles';
import styles from '../../../styles.module.css';
import { getScan } from '../../../services/carotidas';
import { getPrefix } from '../../../util/utils';
import ReportImagem from './ReportImagem';
import LaudoCardHeader from '../../../components/laudos/LaudoCardHeader';
import FileCarousel from '../../../components/laudos/FileCarousel';
import IcmHeaderImage from '../../../components/IcmHeaderImage';

export default function ReportCarotida() {
  const { id } = useParams();
  const location = useLocation();
  const { imagemData, printing = false } = location.state || {};
  const { data: carotida, error, isLoading } = useSWR(
    id ? ['carotida', id] : null,
    () => getScan(id)
  );
  const [showModal, setShowModal] = useState(false);
  const [urls, setURLs] = useState([]);
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [printImagem, setPrintImagem] = useState(true);

  const carotidaComImagemAtual = useMemo(() => {
    if (!carotida || !imagemData) return carotida;

    return {
      ...carotida,
      carotida: [
        {
          ...carotida?.carotida?.[0],
          IMAGEM: imagemData,
        },
        ...(carotida?.carotida?.slice(1) || []),
      ],
    };
  }, [carotida, imagemData]);

  const codScan = carotida?.carotida?.[0]?.CODSCAN;

  useEffect(() => {
    if (!codScan) return;

    async function fetchFiles() {
      try {
        const { data } = await api.get('/files', {
          params: {
            codLaudo: codScan,
            procedimento: 'carotida',
          },
        });

        const filteredFiles = Array.isArray(data)
          ? data.filter(
              item =>
                item.Key.includes('.webm') ||
                item.Key.includes('.pdf') ||
                item.Key.includes('.mp4')
            )
          : [];

        setFiles(filteredFiles);
      } catch (error) {
        toast.error(
          error?.response?.data?.error || 'Erro ao carregar imagens!'
        );
      }
    }

    fetchFiles();
  }, [codScan]);

  async function showFiles() {
    setShowModal(true);
    setLoadingFiles(true);
    try {
      const array = [];

      for (const file of files) {
        const url = await requestLaudoPresignDownload({ key: file.Key });
        array.push({ url, ...file });
      }

      setURLs(array);
    } catch (error) {
      setURLs([]);
      toast.error(error?.response?.data?.error || 'Erro ao carregar imagens!');
    } finally {
      setLoadingFiles(false);
    }
  }

  function removeDiv(divName) {
    document.getElementById(divName).style.visibility = 'hidden';
    window.scrollTo(0, 0);
    window.print();
    document.getElementById(divName).style.visibility = 'visible';
  }

  if (error) {
    return (
      <Alert variant="danger" style={{ marginTop: 10 }}>
        <Alert.Heading>Erro ao carregar laudo!</Alert.Heading>
        <p>Tente novamente.</p>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
        }}
      >
        <Spinner animation="border" variant="dark" size="lg" />
      </div>
    );
  }

  return (
    <div>
      <style>
        {`
          @media print {
            #removeMe {
              display: none;
            }
          }
        `}
      </style>
      <Card className="mt-2">
        <div className={styles.imageReport}>
          <IcmHeaderImage showBlank={!printing} />
        </div>
        <LaudoCardHeader title="ULTRASSOM DOPPLER DE CARÓTIDAS" />
        <Card.Body>
          <Form noValidate className={styles.contentReport}>
            <strong className="mr-auto">Nome do Paciente: </strong>
            {`${carotida?.carotida[0]?.CODPAC} - ${carotida?.carotida[0]?.NOMEPAC}`}
            <br></br>
            <strong className="mr-auto">Data de Nasc: </strong>
            {`${carotida?.carotida[0]?.dataNasc}`}
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <strong className="mr-auto">Sexo: </strong>
            {`${carotida?.carotida[0]?.SEXOPAC}`}
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <strong className="mr-auto">Idade: </strong>
            {`${carotida?.carotida[0]?.idadePAC} anos`}
            <br></br>
            <strong className="mr-auto">Convênio: </strong>
            {carotida?.carotida[0]?.SIGLACONV}
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <strong className="mr-auto">Código: </strong>
            {carotida?.carotida[0]?.CODSCAN}&nbsp;&nbsp;&nbsp;
            <strong className="mr-auto">Data do Laudo: </strong>
            {carotida?.carotida[0]?.dataScanFmt}
            <br></br>
            <strong className="mr-auto">
              Médico que Solicitou:{' '}
              {getPrefix(carotida?.carotida[0]?.CODSOLICITANTE)}{' '}
            </strong>
            {`${carotida?.carotida[0]?.Solicitante}`}
            <Card.Header className="text-center mt-2">
              <strong>
                <u>SISTEMA CAROTÍDEO DIREITO</u>
              </strong>
            </Card.Header>
            <Form.Group size="sm">
              {carotida?.ccd?.length > 0 && (
                <>
                  <u className="mr-auto">Carótida Comum Direita: </u>
                  {carotida?.ccd?.map(ccdir =>
                    ccdir.CODANATOMIA === 44 ? (
                      <div key={ccdir.CODANATOMIA}>
                        {ccdir.ANATOMIA} {ccdir.MEDIDA} mm
                        {ccdir.VELOCIDADE} {ccdir.INTENSIDADE}
                      </div>
                    ) : (
                      <div key={ccdir.CODANATOMIA}>
                        {ccdir.ANATOMIA} {ccdir.VELOCIDADE} {ccdir.INTENSIDADE}
                      </div>
                    )
                  )}
                </>
              )}
            </Form.Group>
            <Form.Group size="sm">
              {carotida?.cid?.length > 0 && (
                <>
                  <u className="mr-auto">Carótida Interna Direita: </u>
                  {carotida?.cid.map(cidir => (
                    <div key={cidir.CODANATOMIA}>
                      {cidir.ANATOMIA} {cidir.VELOCIDADE} {cidir.INTENSIDADE}
                    </div>
                  ))}
                </>
              )}
            </Form.Group>
            <Form.Group size="sm">
              {carotida?.ced?.length > 0 ? (
                <>
                  <u className="mr-auto">Carótida Externa Direita: </u>
                  {carotida?.ced?.map(cedir => (
                    <div key={cedir.CODANATOMIA}>
                      {cedir.ANATOMIA} {cedir.VELOCIDADE} {cedir.INTENSIDADE}
                    </div>
                  ))}
                </>
              ) : null}
            </Form.Group>
            <Form.Group size="sm">
              {carotida?.avd?.length > 0 ? (
                <>
                  <u className="mr-auto">Artéria Vertebral Direita: </u>
                  {carotida?.avd?.map(avdir => (
                    <div key={avdir.CODANATOMIA}>
                      {avdir.ANATOMIA} {avdir.VELOCIDADE} {avdir.INTENSIDADE}
                    </div>
                  ))}
                </>
              ) : null}
            </Form.Group>
            <Card.Header className="text-center">
              <strong>
                <u>SISTEMA CAROTÍDEO ESQUERDO</u>
              </strong>
            </Card.Header>
            <Form.Group size="sm">
              {carotida?.cce?.length > 0 ? (
                <>
                  <u className="mr-auto">Carótida Comum Esquerda: </u>
                  {carotida?.cce?.map(ccesq =>
                    ccesq.CODANATOMIA === 44 ? (
                      <div key={ccesq.CODANATOMIA}>
                        {ccesq.ANATOMIA} {ccesq.MEDIDA} mm
                        {ccesq.VELOCIDADE} {ccesq.INTENSIDADE}
                      </div>
                    ) : (
                      <div key={ccesq.CODANATOMIA}>
                        {ccesq.ANATOMIA} {ccesq.VELOCIDADE} {ccesq.INTENSIDADE}
                      </div>
                    )
                  )}
                </>
              ) : null}
            </Form.Group>
            <Form.Group size="sm">
              {carotida?.cie?.length > 0 && (
                <>
                  <u className="mr-auto">Carótida Interna Esquerda: </u>
                  {carotida?.cie.map(ciesq => (
                    <div key={ciesq.CODANATOMIA}>
                      {ciesq.ANATOMIA} {ciesq.VELOCIDADE} {ciesq.INTENSIDADE}
                    </div>
                  ))}
                </>
              )}
            </Form.Group>
            <Form.Group size="sm">
              {carotida?.cee?.length > 0 ? (
                <>
                  <u className="mr-auto">Carótida Externa Esquerda: </u>
                  {carotida?.cee.map(ceesq => (
                    <div key={ceesq.CODANATOMIA}>
                      {ceesq.ANATOMIA} {ceesq.VELOCIDADE} {ceesq.INTENSIDADE}
                    </div>
                  ))}
                </>
              ) : null}
            </Form.Group>
            <Form.Group size="sm">
              {carotida?.ave?.length > 0 ? (
                <>
                  <u className="mr-auto">Artéria Vertebral Esquerda: </u>
                  {carotida?.ave.map(avesq => (
                    <div key={avesq.CODANATOMIA}>
                      {avesq.ANATOMIA} {avesq.VELOCIDADE} {avesq.INTENSIDADE}
                    </div>
                  ))}
                </>
              ) : null}
            </Form.Group>
            {carotida?.carotida[0]?.OBSERVACOES && (
              <Form.Group size="sm">
                <strong className="mr-auto">Observações</strong>
                <div>
                  {carotida?.carotida[0]?.OBSERVACOES.split('\n').map(
                    (item, i) => {
                      return <div key={i}>{item}</div>;
                    }
                  )}
                </div>
              </Form.Group>
            )}
          </Form>
        </Card.Body>
        {files.length > 0 && (
          <div className={styles.messageReport} style={{ width: '100%' }}>
            Imagens disponíveis no site:{' '}
            <strong>www.icm.com.br (Resultado de Exames)</strong>
            {/* TEMPORÁRIO */}
            <br />
            <span>Código de verificação: {carotida?.carotida[0]?.CODPAC}</span>
            <hr
              style={{
                width: '100%',
                display: 'block',
                margin: '1rem 0',
                borderTop: '1px solid #dee2e6',
              }}
            />
          </div>
        )}
        <div className="text-center">
          <i className="mr-auto">Conferido e assinado eletronicamente por: </i>
          <strong className="mr-auto">
            {getPrefix(carotida?.carotida[0]?.CODREALIZOU)}{' '}
            {carotida?.carotida[0]?.Realizou}
          </strong>
          <br></br>
          <i className="mr-auto">
            Assinatura Digital:{' '}
            {md5(JSON.stringify(carotida?.carotida[0]?.OBSERVACOES || ''))}
          </i>
        </div>
        {printImagem && (
          <div className="page-break">
            <ReportImagem
              carotida={carotidaComImagemAtual || carotida}
              printing={printing}
            />
          </div>
        )}{' '}
        <Card.Footer id="removeMe" className="mt-3 text-center">
          <Button onClick={() => removeDiv('removeMe')}>Imprimir</Button>{' '}
          <Button onClick={() => setPrintImagem(!printImagem)}>
            {printImagem ? 'Imprimir Sem Imagem' : 'Imprimir Com Imagem'}
          </Button>{' '}
          <Button variant="success" onClick={showFiles}>
            Ver imagens antigas
          </Button>
        </Card.Footer>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Imagens</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingFiles ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '50vh',
              }}
            >
              <Spinner animation="border" variant="dark" size="lg" />
            </div>
          ) : (
            <FileCarousel urls={urls} setURLs={setURLs} />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}
